<template>
  <filter-panel
    class="filter-panel"
    @update="applyFilter"
    :filter-parameters="formattedFilters">
    <!-- refresh/auto-refresh feature -->
    <template #panel-actions>
      <div class="request-controls">
        <b-form-checkbox
          class="checkbox"
          @input="() => {}"
          v-model="autoRefresh"
          :disabled="!!getActiveRequestsCount">
          Auto-refresh
        </b-form-checkbox>
        <b-button
          :disabled="!!getActiveRequestsCount"
          @click="forceApplyFilter"
          class="button"
          size="sm"
          :variant="refreshButtonVariant">
          Refresh
        </b-button>
      </div>
    </template>
  </filter-panel>
</template>

<script>
import cloneDeep from "lodash-es/cloneDeep";
import getNested from "lodash-es/get";
import moment from "moment";
import { mapActions, mapGetters } from "vuex";

import FilterPanel, {
  getCurrentAPIFilterValues,
  applyFilterValues
} from "@/components/widgets/FilterPanel";

import {
  getCurrentQuarterRange,
  getCurrentYear,
  getToday,
  getYearToDateRange
} from "@/shared/date-utils";

export default {
  name: "congestion-filters",

  components: {
    FilterPanel
  },

  props: {
    // ...
  },

  data() {
    const ytdRange = getYearToDateRange();

    return {
      filterReady: false,
      autoRefresh: true,
      updatePending: false,
      firstUpdate: true,

      filterSchema: {
        subSegment: {
          name: "subSegment",
          apiName: "subSegment",
          type: "treeselect",
          placeholder: "Segment",
          value: [],
          choices: [],
          rawChoices: [],
          defaultValue: [],
          options: {
            toggleAll: true
          },
        },
        regionId: {
          name: "regionId",
          apiName: "regionId",
          type: "multiselect-region",
          placeholder: "Region",
          value: [],
          choices: [],
          defaultValue: [
            {
              value: "baltic",
              text: "Baltic"
            }
          ],
          options: {
            toggleAll: true
          },
        },
        countryCode: {
          name: "countryCode",
          apiName: "countryCode",
          type: "multiselect",
          placeholder: "Country",
          value: [],
          choices: [],
          defaultValue: [],
          options: {
            toggleAll: true
          },
        },
        portId: {
          name: "portId",
          apiName: "portId",
          type: "multiselect",
          placeholder: "Port",
          value: [],
          choices: [],
          defaultValue: [],
          options: {
            // this field will be displayed on the right of each list items
            valueLabel: "countryCode",
            toggleAll: true,
          },
        },
        commodity: {
          name: "commodity",
          apiName: "commodity",
          type: "treeselect",
          placeholder: "Commodity",
          value: [],
          choices: [],
          rawChoices: [],
          defaultValue: [],
          options: {
            toggleAll: true
          }
        },
        operation: {
          name: "operation",
          apiName: "operation",
          type: "multiselect",
          placeholder: "Operation",
          value: [],
          choices: [
            { text: "Waiting to Load", value: "load" },
            { text: "Waiting to Discharge", value: "discharge" },
            { text: "Waiting for Yard", value: "yard" },
            { text: "Unknown", value: "unknown" }
          ],
          defaultValue: []
        },

        lastLoadCountry: {
          name: "lastLoadCountry",
          apiName: "lastLoadCountry",
          type: "multiselect",
          placeholder: "Last load country",
          value: [],
          choices: [],
          defaultValue: []
        },

        dateRange: {
          name: ["fromDate", "toDate"],
          type: "daterange-picker",
          options: {
            ranges: this.getDatePickerRanges()
          },
          fromDate: {
            name: "fromDate",
            apiName: "fromDate",
            value: moment(ytdRange[0]).format("YYYY-MM-DD")
          },
          toDate: {
            name: "toDate",
            apiName: "toDate",
            value: moment(ytdRange[1]).format("YYYY-MM-DD")
          }
        },

        displayDate: {
          name: "displayDate",
          apiName: "displayDate",
          type: "date",
          label: "Show congestion for date",
          value: moment().format("YYYY-MM-DD"),
          defaultValue: moment().format("YYYY-MM-DD"),
        },
      }
    };
  },

  methods: {
    ...mapActions("api/tradeflows", [
      "getSegments",
      "getRegions",
      "getRegionsWithPolygons",
      "getCountries",
      "getCommodities"
    ]),

    ...mapActions("api/congestion", ["getAnchoragePorts"]),

    async loadFilters() {
      await Promise.all([
        this.loadSegmentChoices(),
        this.loadRegionChoices(),
        this.loadCountryChoices(),
        this.loadPortChoices(),
        this.loadCommodityChoices()
      ]);
      this.filterReady = true;
    },

    async loadSegmentChoices() {
      const response = await this.getSegments({ group: true });
      this.filterSchema.subSegment.choices = response || [];
    },

    async loadRegionChoices() {
      this.filterSchema.regionId.choices =
        (await this.getRegionsWithPolygons()) || [];
    },

    async loadCountryChoices() {
      const response = (await this.getCountries()) || [];
      this.filterSchema.countryCode.choices = response;
      this.filterSchema.lastLoadCountry.choices = response;
    },

    async loadPortChoices() {
      this.filterSchema.portId.choices = (await this.getAnchoragePorts()) || [];
    },

    async loadCommodityChoices() {
      this.filterSchema.commodity.choices =
        (await this.getCommodities({ group: true })) || [];
    },

    getDatePickerRanges() {
      let ranges = {
        "Full period (2015-today)": [
          moment("2015-01-01").toDate(),
          getToday().toDate()
        ],
        QTD: getCurrentQuarterRange(),
        YTD: getYearToDateRange()
      };

      const currentYear = getCurrentYear();
      for (let year = currentYear - 3; year < currentYear; year++) {
        ranges[`${year}`] = [
          moment(`${year}-01-01`).toDate(),
          moment(`${year}-12-31`).toDate()
        ];
      }

      return ranges;
    },

    setDefaultValues() {
      Object.keys(this.filterSchema).forEach(fieldName => {
        const field = this.filterSchema[fieldName];
        field.value = cloneDeep(field.defaultValue);
      });
    },

    applyFilter(filterValues) {
      if (!this.filterReady) {
        return;
      }

      applyFilterValues(this.filterSchema, filterValues);

      if (this.autoRefresh || this.firstUpdate) {
        this.$emit("update", getCurrentAPIFilterValues(this.filterSchema));
        this.updatePending = false;
        this.firstUpdate = false;
      } else {
        this.updatePending = true;
      }
    },

    forceApplyFilter() {
      // apply filter only when all choices are loaded
      this.$emit("update", getCurrentAPIFilterValues(this.filterSchema));
      this.updatePending = false;
    }
  },

  computed: {
    ...mapGetters("api", ["getActiveRequestsCount"]),
    ...mapGetters("api/presets", ["getActivePreset"]),

    refreshButtonVariant() {
      if (!this.autoRefresh && this.updatePending) {
        return "info";
      } else {
        return "outline-secondary";
      }
    },

    formattedFilters() {
      return {
        groups: [
          [
            this.filterSchema.subSegment,
            this.filterSchema.regionId,
            this.filterSchema.countryCode,
            this.filterSchema.portId,
          ],
          [
            this.filterSchema.commodity,
            this.filterSchema.operation,
            this.filterSchema.lastLoadCountry,
            this.filterSchema.dateRange,
          ],
          [
            this.filterSchema.displayDate,
          ]
        ]
      };
    }
  },

  watch: {
    getActivePreset: {
      immediate: true,
      handler: async function(preset) {
        if (preset && this.$route.path !== preset.viewName) {
          return;
        }

        if (!this.filterReady) {
          await this.loadFilters();
          if (!preset) {
            this.setDefaultValues();
          }
        }

        if (preset) {
          const filterValues = getNested(preset, "params.filterPanel", {});
          applyFilterValues(this.filterSchema, filterValues);
        }
      }
    },

    autoRefresh(value) {
      if (value && this.updatePending) {
        this.forceApplyFilter();
      }
    }
  }
};
</script>

<style scoped lang="scss">
.filter-panel {
  margin-bottom: 1.5rem;
}

.request-controls {
  display: flex;
  flex-direction: column;
  margin: auto 0 5px 0;

  .checkbox {
    white-space: nowrap;
    margin-bottom: 5px;
  }
  .button {
    font-size: 0.85rem;
  }
}
</style>
